import { useContext } from "react";
import type { backendReportFinancialRowDTO } from "../../backend/target/typescript/dto";
import { RequestContext } from "../request-context";
import { requestGet, requireResult } from "../utils/network";
import { showBoolean } from "../utils/security";
import { asNumber } from "../utils/strings";
import { getPeriodRange } from "../widget/i-period";
import { Modes } from "../widget/io-abstract";
import IOBoolean from "../widget/io-boolean";
import IONumber from "../widget/io-number";
import SearchPanel, { getPref, type FormValues } from "../widget/search-panel";
import TablePanel from "../widget/table-panel";

type ServerResponse = backendReportFinancialRowDTO[];

export default function ReportFinancial(): React.ReactNode {
  const requestContext = useContext(RequestContext)!;
  const onSearch = (formData: FormValues): Promise<React.ReactNode> =>
    requestContext
      .fetchJSONAndMessage<ServerResponse>(
        "Generating report",
        requestGet("/api/reports/financial", {
          ...formData,
          ...getPeriodRange("year", undefined, undefined, formData["year"])
        })
      )
      .then(requireResult)
      .then((list) => (
        <TablePanel
          rows={list}
          headers={
            <thead>
              <tr>
                <th colSpan={6}>Patient</th>
                <th colSpan={3}>Completion</th>
                <th colSpan={5}>By categories</th>
                <th colSpan={3}>Total</th>
              </tr>
              <tr>
                <th></th>
                <th>Entry Number</th>
                <th>Name</th>
                <th>SL</th>
                <th>Child?</th>
                <th>Poor?</th>
                <th>#Consults</th>
                <th>#Pictures</th>
                <th>Complete?</th>
                <th>Consult</th>
                <th>Medicine</th>
                <th>Surgical</th>
                <th>Workshop</th>
                <th>Others</th>
                <th>Full</th>
                <th>Asked</th>
                <th>Paid</th>
              </tr>
            </thead>
          }
          lineLink={(row) => `/patient/${row.patient_id}`}
          lineColumns={(row) => {
            const consult = asNumber(row.total_consult);
            const medecine = asNumber(row.total_medecine);
            const surgical = asNumber(row.total_surgical);
            const workshop = asNumber(row.total_workshop);
            const other = asNumber(row.total_other);
            const real = asNumber(row.total_real);
            const asked = asNumber(row.total_asked);
            const paid = asNumber(row.total_paid);

            return (
              <>
                <td>{row.reference ?? ""}</td>
                <td>{row.name ?? ""}</td>
                <td>{row.social_level ?? ""}</td>
                <td>{showBoolean(row.first_time_was_child)}</td>
                <td>{showBoolean(row.is_poor)}</td>

                <td>{row.nb_consults ?? ""}</td>
                <td>{row.nb_pictures ?? ""}</td>
                <td>{showBoolean(row.is_complete)}</td>

                <td>{consult || ""}</td>
                <td>{medecine || ""}</td>
                <td>{surgical || ""}</td>
                <td>{workshop || ""}</td>
                <td>{other || ""}</td>
                <td>{real || ""}</td>
                <td>{asked || ""}</td>
                <td>{paid || ""}</td>
              </>
            );
          }}
          footers={
            <tfoot>
              <tr>
                <th colSpan={9}>total</th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_consult),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_medecine),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_surgical),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_workshop),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_other),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_real),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_asked),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_paid),
                    0
                  )}
                </th>
              </tr>
            </tfoot>
          }
          reportBasename={`financial ${formData["period"] == "day" ? formData["day"] : formData["period"] == "month" ? formData["month"] : formData["year"]}`}
        />
      ));

  return (
    <div>
      <h1>
        <img src="/static/img/reports.gif" />
        Financial Report
      </h1>

      <SearchPanel
        form={
          <>
            <IONumber
              name="year"
              mode={Modes.input}
              value={asNumber(
                getPref("year", new Date().getFullYear().toString())
              )}
              required
              htmlProps={{ min: 1990, max: 2100 }}
            />
            <IOBoolean name="is_child" mode={Modes.input} />
            <IOBoolean name="is_poor" mode={Modes.input} />
            <IOBoolean name="is_complete" mode={Modes.input} />
          </>
        }
        onSearch={onSearch}
      />
    </div>
  );
}
